// Phase 12 FIX 3 + Phase 12.4 — Single master guided tour with corrected selectors.
//
// Phase 12.4 audit found multiple steps targeting non-existent selectors which
// silently halted the tour at step 2. Every step below now points at a
// confirmed-present testid OR a `data-tour` attribute on an element we control.

export const TOUR_STORAGE_KEY = "scope_tour_v12";

/** Read tour state from localStorage. */
export function readTourState() {
    try {
        return JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || "{}");
    } catch {
        return {};
    }
}

/** Write tour state to localStorage. */
export function writeTourState(state) {
    try {
        localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(state || {}));
    } catch {
        /* noop */
    }
}

export function markTourDismissed() {
    writeTourState({ ...readTourState(), dismissed_at: new Date().toISOString() });
}

export function markTourCompleted() {
    writeTourState({ ...readTourState(), completed_at: new Date().toISOString() });
}

export function resetTour() {
    writeTourState({});
}

/**
 * Each step:
 *   id:        stable identifier for logging
 *   route:     path to navigate to BEFORE showing this step (optional)
 *   target:    CSS selector (ONE only — no comma-separated fallbacks) or "body" for centered
 *   placement: joyride placement override (optional)
 *   title:     optional bold prefix
 *   content:   the step copy
 */
export function buildTourSteps({ exampleAnalysisId } = {}) {
    const exampleHref = exampleAnalysisId
        ? `/analyses/${exampleAnalysisId}`
        : null;
    return [
        // 1 — welcome (centered modal on home; always resolves)
        {
            id: "welcome",
            route: "/",
            target: "body",
            placement: "center",
            title: "Welcome to Tomauro",
            content:
                "This is a 90-second walkthrough of the entire platform. " +
                "You can leave anytime with Cancel tour or Esc.",
        },
        // 2 — home hero CTA. Uses the stable testid that already exists on Landing.jsx.
        {
            id: "home-cta",
            route: "/",
            target: "[data-testid=\"hero-cta-primary\"]",
            placement: "top",
            content: "The primary call to action drops you straight into a new analysis. The 'How it works' link below it takes you to the visual walkthrough.",
        },
        // 3 — how-it-works TOC. Confirmed `data-testid='hiw-toc'` exists.
        {
            id: "how-it-works-toc",
            route: "/how-it-works",
            target: "[data-testid='hiw-toc']",
            content: "The How-it-works page mirrors this tour in long form. The sticky right-rail TOC lets you jump between sections.",
        },
        // 4 — decision textbox on /analyses/new (auth-gated; logged-in test user always present).
        {
            id: "analyses-textbox",
            route: "/analyses/new",
            target: "[data-testid='decision-textarea']",
            content: "Type any decision in plain English: 'Raise interest rates by 50bps in Q2', 'Acquire Anthropic for $40B', 'Mandate flu shots for healthcare workers'. One to four sentences is plenty.",
        },
        // 5 — enhance button. Actual testid is `enhance-button` (not `enhance-prompt-button`).
        {
            id: "enhance-prompt",
            route: "/analyses/new",
            target: "[data-testid='enhance-button']",
            content: "Click Enhance to let the platform rewrite your decision into a more structured version. Useful when the wording is vague.",
        },
        // 6 — voice mic. Confirmed.
        {
            id: "voice-mic",
            route: "/analyses/new",
            target: "[data-testid='decision-voice-mic']",
            content: "Dictate the decision instead of typing. Works in Chrome, Edge, and Safari. The ⌘ M / Ctrl M shortcut toggles it without taking your hands off the keyboard.",
        },
        // 7 — seed explainer. Confirmed.
        {
            id: "seed-trajectories",
            route: "/analyses/new",
            target: "[data-testid='seed-trajectories-explainer']",
            content: "The seed makes every analysis reproducible. Same inputs plus the same seed produce identical outputs — a regulator or auditor re-running this analysis will get the exact same qualitative outcome bands and brief every time.",
        },
        // 8 — run analysis button. Confirmed.
        {
            id: "run-analysis",
            route: "/analyses/new",
            target: "[data-testid='run-analysis-button']",
            content: "Click Run when you're happy with the decision. The engine takes about 30 seconds to run all seven layers.",
        },
        // 9 — dashboard rail (the actual testid is `dashboard-rail`, not `dashboard-tabs`).
        {
            id: "dashboard-tabs",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='dashboard-rail']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "Every analysis opens to a 10-tab dashboard. The left rail lets you walk Summary → Consequences → Cascades → Modelled paths → Stakeholders → Brief → Audit.",
        },
        // 10 — Consequences tab. Confirmed `tab-consequences`.
        {
            id: "tab-consequences",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='tab-consequences']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "Consequences is the centerpiece — a 12 × 5 heatmap of modelled impact across canonical domains and time horizons.",
        },
        // 11 — Cascades tab.
        {
            id: "tab-cascades",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='tab-cascades']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "Cascades shows how impact in one domain triggers impact in adjacent domains as time progresses.",
        },
        // 12 — Modelled paths tab.
        {
            id: "tab-trajectories",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='tab-trajectories']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "Trajectories is the percentile-fan view: median + 90% interval per domain, time on the X axis.",
        },
        // 13 — Stakeholders tab.
        {
            id: "tab-stakeholders",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='tab-stakeholders']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "Stakeholders identifies who's affected and how, with modelled direction and magnitude per group.",
        },
        // 14 — Brief tab.
        {
            id: "tab-brief",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='tab-brief']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "The Brief is the plain-English executive summary. Read it first if you're short on time.",
        },
        // 15 — Audit Chain tab. Confirmed `tab-audit-chain`.
        {
            id: "tab-audit",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='tab-audit-chain']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "The Audit tab exposes the cryptographic seal: stage hashes, Merkle root, Ed25519 signature. Click Verify locally to confirm the analysis was produced by this engine.",
        },
        // 16 — Counsel FAB. Confirmed on Counsel.jsx.
        {
            id: "counsel-fab",
            route: exampleHref || "/analyses",
            target: "[data-testid='counsel-fab']",
            content: "Counsel is the chat assistant scoped to whatever you're looking at. It only sees this analysis — no external facts, no other workspaces.",
        },
        // 17 — Report (open-as-report) — actual testid on AnalysisDetail.
        {
            id: "report-download",
            route: exampleHref || "/analyses",
            target: exampleHref ? "[data-testid='open-as-report']" : "body",
            placement: exampleHref ? undefined : "center",
            content: "Open the printable Brief. Download as a watermarked PDF with an embedded audit-chain QR code, or share a regulator-portal link.",
        },
        // 18 — (removed 2026-07-27 CHANGE 24 — /calibration page dropped.)
        // 19 — (removed 2026-07-27 CHANGE 2 — Methodology page dropped.)
        // 20 — Shortcuts section on /how-it-works.
        {
            id: "shortcuts",
            route: "/how-it-works",
            target: "[data-testid='hiw-section-shortcuts']",
            content: "Every shortcut is two keys. ⌘ K opens the command palette, ⌘ N starts a new analysis, ⌘ J opens Counsel, Esc closes any panel.",
        },
        // 21 — Restart-tour explainer (centered).
        {
            id: "restart-tour",
            route: "/",
            target: "body",
            placement: "center",
            content: "You can restart this tour any time from the user menu → Take the tour, or by pressing Esc to cancel it.",
        },
        // 22 — Done (centered).
        {
            id: "done",
            route: "/",
            target: "body",
            placement: "center",
            title: "You're set",
            content: "Run your first analysis or explore on your own. Counsel is one shortcut away (⌘ J).",
        },
    ];
}
